import React, { useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  LinearProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import api from "../services/api";

function uploadUrl(filename) {
  if (!filename) return "";
  const apiBase = api.defaults.baseURL || "";
  const host = apiBase.replace(/\/api\/?$/, "");
  return `${host}/uploads/${filename}`;
}

const VERDICT_META = {
  STRONG_MATCH:        { color: "success", label: "Strong match" },
  LIKELY_MATCH:        { color: "primary", label: "Likely match" },
  WEAK_MATCH:          { color: "warning", label: "Weak match" },
  AMBIGUOUS:           { color: "warning", label: "Ambiguous — top results are close" },
  NO_CONFIDENT_MATCH:  { color: "error",   label: "No confident match" },
  EMPTY_LIBRARY:       { color: "default", label: "Library is empty" },
};

export default function WireCompare() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setError("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const compare = async () => {
    if (!file) {
      alert("Pick a wire photo first");
      return;
    }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await api.post("/wireLibrary/compare", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
    } catch (err) {
      console.error("Compare failed:", err);
      setError(err.response?.data?.error || err.message || "Compare failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const verdictMeta = result ? VERDICT_META[result.verdict] || { color: "default", label: result.verdict } : null;

  return (
    <Box p={1}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Identify Wire by Photo</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Upload a photo of a wire and we'll match it against your saved library.
          Tip: a clean close-up of one wire works best.
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<CloudUploadIcon />}
            >
              {file ? `Selected: ${file.name}` : "Select Wire Photo"}
              <input ref={fileRef} type="file" hidden accept="image/*" onChange={onPick} />
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={compare}
              disabled={!file || busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            >
              {busy ? "Comparing..." : "Identify Wire"}
            </Button>
          </Grid>
        </Grid>

        {previewUrl && (
          <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box
              sx={{
                width: 220,
                height: 160,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img src={previewUrl} alt="probe" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Box>
            <Button onClick={reset}>Clear</Button>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      {/* === Result === */}
      {result && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Typography variant="h6">Result</Typography>
            {verdictMeta && (
              <Chip color={verdictMeta.color} label={verdictMeta.label} />
            )}
          </Box>

          {result.verdict === "EMPTY_LIBRARY" ? (
            <Alert severity="info">
              {result.message || "Library is empty. Save some wire reference photos first."}
            </Alert>
          ) : result.matches.length === 0 ? (
            <Alert severity="warning">No matches returned.</Alert>
          ) : (
            <>
              {result.topGuess && (
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Top guess: <strong>{result.topGuess}</strong>
                </Typography>
              )}

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Wire Name</TableCell>
                      <TableCell>Cu %</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Similarity</TableCell>
                      <TableCell>Matched / Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.matches.map((m, idx) => (
                      <TableRow
                        key={m.Wire_ID}
                        sx={idx === 0 ? { bgcolor: "success.light" } : undefined}
                      >
                        <TableCell>{idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`}</TableCell>
                        <TableCell>
                          {m.bestImage && (
                            <img
                              src={uploadUrl(m.bestImage)}
                              alt=""
                              style={{ width: 64, height: 48, objectFit: "cover", borderRadius: 4 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{m.WireName}</TableCell>
                        <TableCell>{m.WirePercentage == null ? "-" : `${m.WirePercentage}%`}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.max(0, Math.min(100, m.similarity))}
                              sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                              color={
                                m.similarity >= 85 ? "success" :
                                m.similarity >= 70 ? "primary" :
                                m.similarity >= 55 ? "warning" : "error"
                              }
                            />
                            <Typography variant="body2" sx={{ minWidth: 48, textAlign: "right" }}>
                              {m.similarity}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{m.matchedSamples} / {m.totalSamples}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                Similarity = best photo match (60%) + average across all reference photos (40%).
                "Matched / Total" counts reference photos scoring ≥ 70%.
              </Typography>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
